import { Link } from "react-router-dom";
import { SiteShell, useSite } from "@/components/site/SiteShell";

const SITE_URL = "www.brionigranite.com";

const Block = ({ n, title, children, testId }) => (
  <section
    className="grid gap-6 border-t border-[color:var(--s-line)] py-12 lg:grid-cols-[7rem_1fr]"
    data-testid={testId}
  >
    <div className="s-num text-2xl text-[color:var(--s-gold)]">
      {String(n).padStart(2, "0")}
    </div>
    <div>
      <h2 className="s-display text-2xl sm:text-3xl">{title}</h2>
      <div className="mt-5 space-y-4 text-sm leading-relaxed text-[color:var(--s-mute)]">
        {children}
      </div>
    </div>
  </section>
);

export default function SiteTerms() {
  const site = useSite();
  const shop = site?.shop || {};
  const name = shop.name || "Brioni Granite";
  const phone = shop.phone || "";
  const email = shop.email || "";

  return (
    <SiteShell site={site} testId="site-terms">
      <section className="px-6 pb-16 pt-6 sm:pt-8 lg:px-12">
        <div className="s-micro text-[color:var(--s-gold)]">Legal</div>
        <h1 className="s-display mt-8 max-w-3xl text-5xl sm:text-6xl">
          Terms &amp; conditions
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)]">
          These terms cover your use of {SITE_URL}, the quotes and work we carry
          out, and the text messages we send. They apply to {name}{" "}
          (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) and to you as
          a customer or visitor.
        </p>
      </section>

      <div className="px-6 pb-28 lg:px-12">
        <Block n={1} title="Who we are" testId="terms-who">
          <p>
            {name} is a supplier and fabricator of natural and engineered stone
            based in St. Louis, Missouri, trading through {SITE_URL}. You can
            reach us on {phone || "our shop number"}
            {email ? ` or at ${email}` : ""} during business hours.
          </p>
        </Block>

        <Block n={2} title="Quotes and pricing" testId="terms-quotes">
          <p>
            Quotes are based on the plans, sketches or measurements you give us
            and are valid for 30 days unless the quote says otherwise. Final
            figures are confirmed after we digitally template your cabinets, and
            square footage, edge work, cut-outs and material availability can
            change the price.
          </p>
          <p>
            Slab prices move with our containers. A quote does not hold a slab —
            a piece is only reserved once a hold is confirmed in writing or in
            your customer portal.
          </p>
        </Block>

        <Block n={3} title="Deposits and payment" testId="terms-payment">
          <p>
            A deposit is required before fabrication begins. The balance is due
            on completion unless we have agreed different terms in writing. Card
            payments are processed by our payment provider; we never see or
            store your full card details.
          </p>
          <p>
            Material remains our property until the invoice is paid in full.
          </p>
        </Block>

        <Block
          n={4}
          title="Material, color and natural variation"
          testId="terms-material"
        >
          <p>
            Granite, quartzite and marble are natural products. Veining,
            movement, pitting and color vary from slab to slab and between
            photographs and the real piece. Photos on {SITE_URL} and in your
            portal are a guide — we always recommend seeing the actual slab in
            our warehouse before we cut.
          </p>
          <p>
            Engineered and sintered surfaces can also vary between production
            batches. Seam placement is shown on your drawing for approval; some
            seams are unavoidable because of slab size and access.
          </p>
        </Block>

        <Block
          n={5}
          title="Templating, fabrication and installation"
          testId="terms-install"
        >
          <p>
            Templating happens once your cabinets are set and level. Nothing is
            cut until you approve the shop drawing. Changes after approval may
            add cost and time.
          </p>
          <p>
            On install day the work area must be clear and accessible. We are
            not responsible for plumbing, electrical or gas disconnection and
            reconnection, tile or drywall repairs, or damage caused by unlevel
            or unsuitable cabinets and substructures.
          </p>
        </Block>

        <Block n={6} title="Lead times" testId="terms-lead-times">
          <p>
            Dates we give are our best estimate and are not guaranteed. Weather,
            container delays, cabinet delays and site readiness all affect the
            schedule. We keep you updated through your portal, by email or by
            text.
          </p>
        </Block>

        <Block n={7} title="Warranty" testId="terms-warranty">
          <p>
            We warrant our fabrication and installation workmanship for one year
            from install. The warranty does not cover damage from misuse, heat,
            impact, harsh chemicals, staining, settlement of the building, or
            work altered by anyone else. Natural variation is not a defect.
          </p>
          <p>
            Manufactured surfaces carry the manufacturer&rsquo;s own warranty,
            which we register or pass on to you where it applies.
          </p>
        </Block>

        <Block n={8} title="Remnants and offcuts" testId="terms-remnants">
          <p>
            Remnants are sold as seen, in the size and condition shown. Offcuts
            from your job remain our property unless we agree otherwise in
            writing.
          </p>
        </Block>

        <Block n={9} title="Text message (SMS) terms" testId="terms-sms">
          <p>
            <strong className="text-[color:var(--s-ink)]">
              Program description.
            </strong>{" "}
            {name} sends text messages about your enquiry, quote, slab holds,
            templating and installation appointments, delivery and job updates,
            and replies to questions you send us. This is a conversational
            service between you and our shop — not a marketing blast.
          </p>
          <p>
            <strong className="text-[color:var(--s-ink)]">Consent.</strong> By
            giving us your mobile number on {SITE_URL}, on a quote request, in
            your customer portal, or in person, you agree that {name} may
            contact you by text at that number. Consent is not a condition of
            any purchase. You may ask us to call or email instead at any time.
          </p>
          <p>
            <strong className="text-[color:var(--s-ink)]">
              Message frequency.
            </strong>{" "}
            Message frequency varies with the stage of your project — typically
            a handful of messages around quoting, templating and install day.
          </p>
          <p>
            <strong className="text-[color:var(--s-ink)]">Cost.</strong> Message
            and data rates may apply. {name} does not charge for text messages;
            your mobile carrier&rsquo;s standard rates apply.
          </p>
          <p>
            <strong className="text-[color:var(--s-ink)]">
              Opting out and help.
            </strong>{" "}
            Reply <strong className="text-[color:var(--s-ink)]">STOP</strong> to
            any message to stop receiving texts from us. You will get one
            confirmation message and no further texts. Reply{" "}
            <strong className="text-[color:var(--s-ink)]">HELP</strong> for
            help, or call us on {phone || "our shop number"}. You can opt back
            in at any time by texting us again or asking the shop.
          </p>
          <p>
            <strong className="text-[color:var(--s-ink)]">
              Carriers and delivery.
            </strong>{" "}
            Mobile carriers are not liable for delayed or undelivered messages.
            Texting is not a secure or guaranteed channel — for anything urgent,
            please call the shop.
          </p>
          <p>
            <strong className="text-[color:var(--s-ink)]">
              Your mobile number.
            </strong>{" "}
            We do not sell or rent mobile numbers, and we do not share mobile
            numbers or text-message consent with third parties or affiliates for
            their own marketing. Numbers are shared only with the messaging
            provider that delivers our texts on our behalf.
          </p>
          <p>
            Supported carriers include the major US networks. Availability can
            vary by carrier and is outside our control.
          </p>
        </Block>

        <Block n={10} title="Email and your portal" testId="terms-email">
          <p>
            We email quotes, drawings for approval, invoices and job updates,
            and we give you a private customer portal link. Keep that link to
            yourself — anyone with it can see your project. We never ask for
            your password or card details by email or text.
          </p>
        </Block>

        <Block n={11} title="Privacy" testId="terms-privacy">
          <p>
            We collect only what we need to quote and complete your work: your
            name, contact details, address, project details and photos of your
            job. We keep it for our records and legal obligations, and we do not
            sell it. Photos of finished work are only published on {SITE_URL}
            when we have your say-so.
          </p>
          <p>
            To ask what we hold, or to have your details removed, contact us on{" "}
            {phone || "our shop number"}
            {email ? ` or ${email}` : ""}.
          </p>
        </Block>

        <Block n={12} title="Website content" testId="terms-website">
          <p>
            Photographs, drawings, slab listings and text on {SITE_URL} belong
            to {name} and may not be reused without our permission. Stock
            quantities and colors shown are live from our warehouse system but
            can change during the day — availability is confirmed when a hold is
            placed.
          </p>
        </Block>

        <Block n={13} title="Changes to these terms" testId="terms-changes">
          <p>
            We may update these terms as our services change. The version on{" "}
            {SITE_URL} at the time you accept a quote is the one that applies to
            your job. These terms are governed by the laws of the State of
            Missouri.
          </p>
        </Block>

        <div className="border-t border-[color:var(--s-line)] pt-12">
          <p className="text-sm text-[color:var(--s-mute)]">
            Questions about any of this? Call {phone || "the shop"} or{" "}
            <Link
              to="/contact"
              className="text-[color:var(--s-gold)] underline"
            >
              send us a message
            </Link>
            .
          </p>
        </div>
      </div>
    </SiteShell>
  );
}
